import React, {PureComponent} from 'react';

import {IPageResponse, SortDirection} from "../common/interfaces/IPageResponse";
import DocumentService from "../services/document.service";
import {
    DocumentRowFilterType,
    IDocumentRow,
    IDocumentRowRequest,
    IDocumentRowSortFields
} from "../models/document-row";
import {DocumentRowSearchBar} from "../components/DocumentRows/DocumentRowSearchBar";
import {DocumentRowTable} from "../components/DocumentRows/DocumentRowTable";
import {AuthenticationContext} from "../services/context/authentication.context";
import { DATE_PRESET_OPTIONS } from 'models/calendar';
import { DateRangeInput } from 'components/DateRangePicker/DateRangePicker';
import { convertDocumentRowsToSheet, saveAsXLSX } from 'helpers/xlsx';
import { savePdf } from 'helpers/pdf';
import { Button, Spinner } from 'react-bootstrap';

export interface IDocumentRowsState {
    customerId: number | null;
    documentRows: IPageResponse<IDocumentRow> | null;
    selectedRows: IDocumentRow[];
    pageRequest: IDocumentRowRequest;
    filters: { [key in DocumentRowFilterType]?: any };
    isRequesting: boolean;
    downloadingDocumentXlsx: boolean;
    downloadingDocumentPdf: boolean | number;
    downloadingDocumentZip: boolean;
}

const defaultFilters = {
    [DocumentRowFilterType.CARD]: null,
    [DocumentRowFilterType.LOCATION]: null,
    [DocumentRowFilterType.NAME]: null,
    [DocumentRowFilterType.DATE]: DATE_PRESET_OPTIONS[4]
};

const defaultRequest = {
    page: 0,
    size: 20,
    sortDirection: SortDirection.DESC,
    sortField: IDocumentRowSortFields.DOCUMENT_DATE,
    documentDateCreatedStart: DATE_PRESET_OPTIONS[4].startDate.format('YYYY-MM-DD'),
    documentDateCreatedEnd: DATE_PRESET_OPTIONS[4].endDate.format('YYYY-MM-DD'),
    types: 'LV_5,LV_5A,LV_5E,RM_5A'
};

export class DocumentRow extends PureComponent<any, IDocumentRowsState> {

    static contextType = AuthenticationContext;
    constructor(props: any) {
        super(props);
        this.state = {
            customerId: null,
            documentRows: null,
            selectedRows: [],
            isRequesting: false,
            downloadingDocumentXlsx: false,
            downloadingDocumentPdf: false,
            downloadingDocumentZip: false,
            pageRequest: {
                page: 0,
                size: 20,
                sortDirection: SortDirection.DESC,
                sortField: IDocumentRowSortFields.DOCUMENT_DATE,
                documentDateCreatedStart: DATE_PRESET_OPTIONS[4].startDate.format('YYYY-MM-DD'),
                documentDateCreatedEnd: DATE_PRESET_OPTIONS[4].endDate.format('YYYY-MM-DD'),
                types: 'LV_5,LV_5A,LV_5E,RM_5A'
            },
            filters: {
                [DocumentRowFilterType.CARD]: null,
                [DocumentRowFilterType.LOCATION]: null,
                [DocumentRowFilterType.NAME]: null,
                [DocumentRowFilterType.DATE]: DATE_PRESET_OPTIONS[4],
                [DocumentRowFilterType.VEHICLE_NUMBER]: null
            }            
        };
        this.props.onLoadValidatePage();
    }

    getDocumentRows = () => {
        if (this.state.customerId === null)
            return;
        this.setState({ isRequesting: true });
        DocumentService.getDocumentRows(this.state.customerId, this.state.pageRequest).then((response: IPageResponse<IDocumentRow>) => {
            this.setState({
                documentRows: response,
                isRequesting: false
            });
        });
    };

    onPageChange = (targetPage: number) => {
        if (this.state.pageRequest.page !== targetPage - 1) {
            this.setState({
                ...this.state,
                pageRequest: {
                    ...this.state.pageRequest,
                    page: targetPage - 1
                }
            }, () => {
                this.getDocumentRows();
            });
        }
    };

    updateCustomerId = () => {
        if (this.context.state.customerId !== this.state.customerId) {
            this.setState({
                ...this.state,
                customerId: this.context.state.customerId,
                pageRequest: defaultRequest,
                filters: defaultFilters
            }, () => {
                this.getDocumentRows();
            });
        }
    };

    componentDidMount(): void {
        this.updateCustomerId();
    }

    componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<IDocumentRowsState>, snapshot?: any): void {
        this.updateCustomerId();
    }

    onFilterChange = (type: DocumentRowFilterType, value: any) => {
        const pageRequest = {
            ...this.state.pageRequest,
            page: 0
        };
        let nameVal = this.state.filters.NAME;
        if (type === DocumentRowFilterType.DATE) {
            const val = value as DateRangeInput;
            value.id = val.selectedOptionId;
            pageRequest.documentDateCreatedStart = val.startDate.format('YYYY-MM-DD');
            pageRequest.documentDateCreatedEnd = val.endDate.format('YYYY-MM-DD');
        } else if (type === DocumentRowFilterType.CARD){
            pageRequest.limitToCard = (value && value.value.id) || undefined;
        } else if (type === DocumentRowFilterType.LOCATION) {
            pageRequest.limitToLocation = (value && value.value) || undefined;
        } else if (type === DocumentRowFilterType.NAME) {
            pageRequest.limitToCardName = (value && value.value) || undefined;
            nameVal = value;
        } else if (type === DocumentRowFilterType.VEHICLE_NUMBER) {
            pageRequest.vehicleNumber = (value && value.value) || undefined;
        }
        this.setState({
            filters: {
                ...this.state.filters,
                [type]: value,
                NAME: type === DocumentRowFilterType.CARD && value ? { value: value.value.name, label: value.value.name } : nameVal
            },
            pageRequest
        }, () => this.getDocumentRows());
    }

    onSortChange = (sortedByField: IDocumentRowSortFields, sortDirection: SortDirection) => {
        this.setState({
            ...this.state,
            pageRequest: {
              ...this.state.pageRequest,
                sortDirection: sortDirection,
                sortField: sortedByField
            }
        }, () => {
            this.getDocumentRows();
        });
    };

    getDocumentRowsFile = (type: 'XLSX' | 'PDF') => {
        if (this.state.customerId === null || !this.state.documentRows)
            return;
        
        if (type === 'XLSX') {
            this.setState({ downloadingDocumentXlsx: true });
        } else {
            this.setState({ downloadingDocumentPdf: true });
        }

        DocumentService.getDocumentRows(this.state.customerId, {
            ...this.state.pageRequest,
            page: 0,
            size: this.state.documentRows.totalElements || 1
        }).then((response: IPageResponse<IDocumentRow>) => {
            if (type === 'PDF') {
                savePdf(this.context.state.customer.name, this.state.filters, response.content, 'Väljavõte');
                this.setState({ downloadingDocumentPdf: false });
            } else {
                saveAsXLSX(convertDocumentRowsToSheet(this.context.state.customer.name, this.state.filters, response.content), 'Väljavõte');
                this.setState({ downloadingDocumentXlsx: false });
            }
        });
    }

    onRowSelect = (row: IDocumentRow | null, state: boolean) => {
        if (state === false) {
            if (!row) {
                this.setState({
                    ...this.state,
                    selectedRows: []
                })
            } else {
                const filtered = this.state.selectedRows.filter(cp => cp.remoteDocumentId !== row.remoteDocumentId);
                this.setState({
                    ...this.state,
                    selectedRows: [...filtered]
                });
            }
        } else if (!row) {
            this.setState({
                ...this.state,
                selectedRows: [...this.state.documentRows?.content ?? []]
            });
        } else {
            this.setState({
                ...this.state,
                selectedRows: [...this.state.selectedRows, row]
            });
        }
    };

    getSelectedRowsZip = () => {
        if (!this.state.selectedRows?.length) {
            return;
        }
        this.setState({
            ...this.state,
            downloadingDocumentZip: true
        });
        DocumentService.getDocumentZipFileWithDownload(
            this.state.selectedRows.map(r => r.documentId).filter((v, i, arr) => arr.indexOf(v) === i),
            'arved'
        )
        .finally(() => {
            this.setState({
                ...this.state,
                downloadingDocumentZip: false
            });
        });
    }

    getDocumentRowFile = (documentRow: IDocumentRow) => {
        this.setState({
            ...this.state,
            downloadingDocumentPdf: documentRow.remoteDocumentId
        });
        DocumentService.getDocumentFileWithDownload(documentRow.remoteDocumentId)
            .finally(() => this.setState({
                ...this.state,
                downloadingDocumentPdf: false
            }));
    }

    render() {

        return (
            <div className="document-rows__container">
                <DocumentRowSearchBar
                    onFilterChange={this.onFilterChange}
                    getDocumentRowsXlsx={() => this.getDocumentRowsFile('XLSX')}
                    getDocumentRowsPdf={() => this.getDocumentRowsFile('PDF')}
                    getSelectedRowsZip={this.getSelectedRowsZip}
                    downloadingDocumentXlsx={this.state.downloadingDocumentXlsx}
                    downloadingDocumentPdf={this.state.downloadingDocumentPdf}
                    filters={this.state.filters}
                    selectedRows={this.state.selectedRows}
                />
                <div className="mt-n4" style={{ visibility: !this.state.selectedRows?.length ? 'hidden' : 'visible' }}>
                    <Button
                        hidden={!this.state.selectedRows?.length}
                        size="sm"
                        variant="link"
                        className="d-flex align-items-center px-1 ml-2 position-relative"
                        onClick={() => this.getSelectedRowsZip()}
                        disabled={this.state.downloadingDocumentZip}
                    >
                        {
                            this.state.downloadingDocumentZip ?
                            <Spinner size="sm" animation="border" /> :
                            <div className="" style={{ color: 'black' }}>
                                <div className='icon__download action-icon icon--16' />
                            </div>
                        } Laadi alla valitud arved ({ this.state.selectedRows.length })
                    </Button>
                </div>
                
                <DocumentRowTable
                    documentRows={this.state.documentRows}
                    onPageChange={this.onPageChange}
                    onSortChange={this.onSortChange}
                    isRequesting={this.state.isRequesting}
                    selectedRows={this.state.selectedRows}
                    onRowSelect={this.onRowSelect}
                    getDocumentRowFile={this.getDocumentRowFile}
                    downloadingDocumentPdf={this.state.downloadingDocumentPdf}
                />
            </div>
        );
    }
}